import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useScrollToTop } from './hooks/useScrollToTop';
import Navbar from './components/Navbar';
import Home from './pages/home/Home';
import Products from './pages/order/Products';
import ProductDetail from './pages/order/ProductDetail';
import Cart from './pages/cart/Cart';
import Checkout from './pages/checkout/Checkout';
import Login from './pages/authentication/Login';
import Register from './pages/authentication/Register';
import ForgotPassword from './pages/authentication/ForgotPassword';
import VerifyOTP from './pages/authentication/VerifyOTP';
import ResetPassword from './pages/authentication/ResetPassword';
import Footer from './components/Footer';
import { CartProvider } from './context/CartContext';
import Search from './pages/order/Search';
import { WishlistProvider } from './context/WishlistContext';
import Profile from './pages/authentication/Profile';
import Dashboard from './pages/admin/Dashboard';
import AdminRoute from './components/AdminRoute';
import Wishlist from './pages/order/Wishlist';
import HomeData from './pages/admin/HomeData';
import Users from './pages/admin/Users';
import AdminProducts from './pages/admin/Products';
import AdminBarcodes from './pages/admin/Barcodes';
import Banners from './pages/admin/Banners';
import Events from './pages/admin/Events';
import Blogs from './pages/admin/Blogs';
import Categories from './pages/admin/Categories';
import SubcategoriesAdmin from './pages/admin/Subcategories';
import Orders from './pages/admin/Orders';
import Support from './pages/Support';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CancellationPolicy from './pages/CancellationPolicy';
import RentSubmit from './pages/order/RentSubmit';
import AdminRentalOrders from './pages/admin/AdminRentalOrders';
import RentalListing from './pages/admin/RentalListing';
import RentalTerms from './pages/RentalTerms';
import CataloguePage from './pages/catalogue/Catalogue';
import ReviewsPage from './pages/reviews/Reviews';
import AdminCatalogue from './pages/admin/Catalogue';
import GoogleReviewsAdmin from './pages/admin/GoogleReviews';
import NotFound from './pages/NotFound';
import { AuthProvider } from './context/AuthContext';

// Import global styles
import './styles/global.css';

// ScrollToTop component to handle scroll behavior
const ScrollToTop = () => {
  useScrollToTop();
  
  // Additional aggressive scroll to top on every route change
  React.useEffect(() => {
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Immediate scroll
    forceScrollToTop();
    
    // Multiple fallbacks
    setTimeout(forceScrollToTop, 0);
    setTimeout(forceScrollToTop, 50);
    setTimeout(forceScrollToTop, 100);
    setTimeout(forceScrollToTop, 200);
    setTimeout(forceScrollToTop, 500);
  });
  
  return null;
};

const RentalListingPlaceholder = () => <div>Rental Listing Coming Soon</div>;

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <AuthProvider>
    <WishlistProvider>
      <CartProvider>
          <ThemeProvider theme={theme}>
        <Router>
          <ScrollToTop />
          <Helmet>
            <title>theVastra.store - Premium Fashion Store</title>
            <meta name="description" content="theVastra.store - Your Premium Fashion Destination. Discover trendy clothing, traditional wear, and exclusive collections. Shop the latest styles with free shipping on orders above ₹1999." />
          </Helmet>
          <div className="min-h-screen flex flex-col bg-gray-50">
            <Navbar />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/verify-otp" element={<VerifyOTP />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/search" element={<Search />} />
                <Route path="/rent/submit" element={<RentSubmit />} />
                <Route path="/rental-terms" element={<RentalTerms />} />
                <Route path="/catalogue" element={<CataloguePage />} />
                <Route path="/reviews" element={<ReviewsPage />} />
                <Route path="/admin" element={<AdminRoute><Dashboard /></AdminRoute>}>
                  <Route index element={<Navigate to="/admin/home" replace />} />
                  <Route path="home" element={<HomeData />} />
                  <Route path="users" element={<Users />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="barcodes" element={<AdminBarcodes />} />
                  <Route path="banners" element={<Banners />} />
                  <Route path="events" element={<Events />} />
                  <Route path="blogs" element={<Blogs />} />
                  <Route path="categories" element={<Categories />} />
                  <Route path="subcategories" element={<SubcategoriesAdmin />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="rental-orders" element={<AdminRentalOrders />} />
                  <Route path="rental-listing" element={<RentalListing />} />
                  <Route path="catalogue" element={<AdminCatalogue />} />
                  <Route path="google-reviews" element={<GoogleReviewsAdmin />} />
                </Route>
                <Route path="/support" element={<Support />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </Router>
        </ThemeProvider>
      </CartProvider>
    </WishlistProvider>
    </AuthProvider>
  );
}

export default App;
